"use client";

import React from "react";
import {AlertCircle, Inbox} from "lucide-react";
import GradientSpinner from "@/shared/components/GradientSpinner/GradientSpinner";
import {cn} from "@/shared/lib/utils";

export type ReactTableStateOverlayVariant = "loading" | "empty" | "error";

export interface ReactTableStateOverlayProps {
    variant: ReactTableStateOverlayVariant;
    /** Komunikat dla wariantu error; dla empty używany jest domyślny "Brak elementów" jeśli nie podano */
    message?: string;
    /** Rozmiar spinnera (loading) i ikon (empty, error) w px */
    iconSize?: number;
    className?: string;
}

const DEFAULT_EMPTY_MESSAGE = "Brak elementów";
const DEFAULT_ERROR_MESSAGE = "Nie udało się załadować danych";

export function ReactTableStateOverlay({
                                           variant,
                                           message,
                                           iconSize = 56,
                                           className,
                                       }: ReactTableStateOverlayProps) {
    const text =
        variant === "error"
            ? message ?? DEFAULT_ERROR_MESSAGE
            : variant === "empty"
                ? message ?? DEFAULT_EMPTY_MESSAGE
                : null;

    return (
        <div
            className={cn(
                "absolute inset-0 z-10 flex items-center justify-center bg-blue-900/20 pointer-events-none",
                className
            )}
        >
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
                {variant === "loading" && (
                    <GradientSpinner size={Math.max(iconSize, 200)} strokeWidth={14}/>
                )}
                {variant === "empty" && (
                    <Inbox
                        className="shrink-0 text-muted-foreground"
                        size={iconSize}
                        strokeWidth={1.5}
                        aria-hidden
                    />
                )}
                {variant === "error" && (
                    <AlertCircle
                        className="shrink-0 text-destructive"
                        size={iconSize}
                        strokeWidth={1.5}
                        aria-hidden
                    />
                )}
                {text && (
                    <span
                        className={cn(
                            "text-center text-base font-medium sm:text-lg",
                            variant === "error" && "text-destructive"
                        )}
                    >
                        {text}
                    </span>
                )}
            </div>
        </div>
    );
}
